import { BooleanQs, type PlatformBusClass, type SearchInterpretResponseDto, SortOrder } from '~/apiClient';
import { FE_SORT_PARAM, SORT_ORDER_DESC_PREFIX } from '~/constants/sorting';

export type InterpretedSlpRoute = Readonly<{
    platformClass?: PlatformBusClass;
    brandUrlKey?: string;
    query: Partial<Record<SlpQueryKey, string | number | string[]>>;
}>;

const isSlpRouteParam = (key: string): key is keyof typeof SLP_QUERY_KEYS => key in SLP_QUERY_KEYS;

/**
 * The interpreted filters laid out the way the SLP writes its own URL: the first class and brand in the path, every
 * other value in the query. Null when the interpretation holds nothing the listing can filter on.
 */
export const getInterpretedSlpRoute = (interpretation: SearchInterpretResponseDto): InterpretedSlpRoute | null => {
    const { platformClass, brandUrlKeys, features, state, sortBy, sortOrder, ...filters } = interpretation;
    const [mainPlatformClass, ...otherPlatformClasses] = platformClass;
    const { pathBrandUrlKey, queryBrandUrlKeys } = splitBrandUrlKeys(brandUrlKeys);
    const query: InterpretedSlpRoute['query'] = {};

    for (const [key, value] of Object.entries(filters)) {
        const isSet = Array.isArray(value) ? value.length > 0 : value !== undefined;

        if (isSet && isSlpRouteParam(key)) query[SLP_QUERY_KEYS[key]] = Array.isArray(value) ? value.map(String) : value;
    }

    for (const feature of features) {
        if (isSlpRouteParam(feature)) query[SLP_QUERY_KEYS[feature]] = BooleanQs.true;
    }

    if (otherPlatformClasses.length) query[SLP_QUERY_KEYS.platformClass] = otherPlatformClasses;
    if (queryBrandUrlKeys) query[SLP_QUERY_KEYS.brandUrlKeys] = queryBrandUrlKeys;
    if (state.length) query[SLP_QUERY_KEYS.aState] = state;
    if (sortBy) query[FE_SORT_PARAM] = `${sortOrder === SortOrder.desc ? SORT_ORDER_DESC_PREFIX : ''}${sortBy}`;

    if (!mainPlatformClass && !pathBrandUrlKey && !Object.keys(query).length) return null;

    return { platformClass: mainPlatformClass, brandUrlKey: pathBrandUrlKey, query };
};
